import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { throwApiError } from "../../../api/api";
import type { RootState } from "../../../store/store";

import searchSvg from "../../../img/search.svg";

import { getStoresPaginatedApi, StorePublic } from "../../../views/store/storeApi";
import {
  assignMeToStoreApi,
  getMyFreelancerStoresApi,
  unassignMeFromStoreApi,
} from "../../../views/freelancer/freelancerApi";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onAssigned?: () => void;
};

const STORES_LIMIT = 10;

const AssignFreelancersToStoresModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onAssigned,
}) => {
  const token = useSelector((state: RootState) => state.freelancerAuth.accessToken);

  const [storesPage, setStoresPage] = useState(1);
  const [storesTotal, setStoresTotal] = useState(0);
  const [stores, setStores] = useState<StorePublic[]>([]);
  const [loadingStores, setLoadingStores] = useState(false);
  const [storeSearchInput, setStoreSearchInput] = useState<string>("");
  const [storeSearch, setStoreSearch] = useState<string>("");

  const [loadingMyStores, setLoadingMyStores] = useState(false);
  const [initialStoreIds, setInitialStoreIds] = useState<Set<string>>(new Set());
  const [selectedStoreIds, setSelectedStoreIds] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  const storesTotalPages = useMemo(
    () => Math.max(1, Math.ceil(storesTotal / STORES_LIMIT)),
    [storesTotal],
  );

  const hasChanges = useMemo(() => {
    if (initialStoreIds.size !== selectedStoreIds.size) return true;
    return Array.from(selectedStoreIds).some((id) => !initialStoreIds.has(id));
  }, [initialStoreIds, selectedStoreIds]);

  const canSubmit = useMemo(() => {
    if (saving) return false;
    return hasChanges;
  }, [saving, hasChanges]);

  const fetchMyStores = useCallback(async () => {
    if (!token) return;

    try {
      setLoadingMyStores(true);
      const data = await getMyFreelancerStoresApi(token);
      const ids = new Set((data.items ?? []).map((s) => s.id));
      setInitialStoreIds(ids);
      setSelectedStoreIds(new Set(ids));
    } catch (error) {
      throwApiError(error, "Erro ao buscar suas lojas.");
      toast.error("Erro ao buscar suas lojas.");
    } finally {
      setLoadingMyStores(false);
    }
  }, [token]);

  const fetchStores = useCallback(
    async (page: number) => {
      if (!token) return;

      try {
        setLoadingStores(true);
        const nome = storeSearch.trim();
        const data = await getStoresPaginatedApi(token, { page, limit: STORES_LIMIT, ...(nome ? { nome } : {}) });
        setStores(data.items);
        setStoresTotal(data.total);
      } catch (error) {
        toast.error("Erro ao buscar lojas.");
      } finally {
        setLoadingStores(false);
      }
    },
    [token, storeSearch],
  );

  const applyStoreSearch = () => {
    setStoresPage(1);
    setStoreSearch(storeSearchInput);
  };

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !saving) onClose();
    };

    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose, saving]);

  useEffect(() => {
    if (!isOpen) return;

    if (!token) {
      toast.error("Sessão inválida. Faça login novamente.");
      onClose();
      return;
    }

    setStoresPage(1);
    setStoresTotal(0);
    setStores([]);
    setInitialStoreIds(new Set());
    setSelectedStoreIds(new Set());
    setSaving(false);
    setStoreSearchInput("");
    setStoreSearch("");

    fetchMyStores();
  }, [isOpen, token, fetchMyStores, fetchStores, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    fetchStores(storesPage);
  }, [isOpen, storesPage, fetchStores]);

  const handleOverlayClick = () => {
    if (!saving) onClose();
  };

  const handleModalClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    e.stopPropagation();
  };

  const toggleStore = (storeId: string) => {
    setSelectedStoreIds((prev) => {
      const next = new Set(prev);
      if (next.has(storeId)) next.delete(storeId);
      else next.add(storeId);
      return next;
    });
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    if (!token) return;

    const toAssign = Array.from(selectedStoreIds).filter((id) => !initialStoreIds.has(id));
    const toUnassign = Array.from(initialStoreIds).filter((id) => !selectedStoreIds.has(id));

    if (toAssign.length === 0 && toUnassign.length === 0) {
      onClose();
      return;
    }

    try {
      setSaving(true);

      const tasks: Promise<unknown>[] = [];

      for (const storeId of toAssign) {
        tasks.push(assignMeToStoreApi(token, { storeId }));
      }

      for (const storeId of toUnassign) {
        tasks.push(unassignMeFromStoreApi(token, storeId));
      }

      await Promise.all(tasks);

      toast.success("Loja(s) atualizada(s) com sucesso");
      onAssigned?.();
      onClose();
    } catch (error) {
      toast.error("Erro ao cadastrar loja(s)");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const isLoading = loadingMyStores || loadingStores;

  return (
    <div className="ModalOverlay" onClick={handleOverlayClick} role="presentation">
      <div className="ModalShell" onClick={handleModalClick} role="dialog" aria-modal="true">
        <h2 className="ModalTitle">Preferência de Lojas</h2>

        <form className="ModalBody" onSubmit={handleSubmit}>
          <div className="Box">
            <div className="BoxTitle">
              Selecione as lojas de sua preferência ({selectedStoreIds.size} selecionada
              {selectedStoreIds.size === 1 ? "" : "s"})
            </div>

            <div className="FreelancerSearchRow">
              <input
                className="SlotInputSearch"
                type="text"
                value={storeSearchInput}
                onChange={(e) => setStoreSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    applyStoreSearch();
                  }
                }}
                disabled={isLoading || saving}
              />
              <button
                type="button"
                className="FreelancerSearchButton"
                onClick={applyStoreSearch}
                disabled={isLoading || saving}
              >
                <img src={searchSvg} alt="Buscar" className="FreelancerSearchIcon" />
              </button>
            </div>

            {isLoading ? (
              <div className="BoxEmpty">Carregando lojas...</div>
            ) : stores.length === 0 ? (
              <div className="BoxEmpty">
                {storeSearch.trim()
                  ? "Nenhuma loja encontrada para essa busca."
                  : "Nenhuma loja encontrada."}
              </div>
            ) : (
              <div className="List">
                {stores.map((s) => {
                  const checked = selectedStoreIds.has(s.id);
                  return (
                    <button
                      key={s.id}
                      type="button"
                      className={`ListItem CheckItem ${checked ? "checked" : ""}`}
                      onClick={() => toggleStore(s.id)}
                      disabled={saving}
                    >
                      <div className="CheckLeft">
                        <input type="checkbox" checked={checked} readOnly className="Checkbox" />
                      </div>

                      <div className="CheckRight">
                        <div className="ListItemTitle">
                          {s.nomeFantasia}
                          {(() => {
                            const uf = (s.estado ?? "").trim().toUpperCase();
                            const nome =
                              uf === "RJ" ? "Rio de Janeiro" :
                              uf === "SP" ? "São Paulo" :
                              "";
                            return nome ? ` • ${nome}` : "";
                          })()}
                        </div>
                        <div className="ListItemSub">
                          {s.endereco ? s.endereco : "Endereço não informado"}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="Pager">
            <button
              type="button"
              className="PagerButton PagerButtonLight"
              disabled={isLoading || storesPage <= 1 || saving}
              onClick={() => setStoresPage((p) => Math.max(1, p - 1))}
            >
              Ant.
            </button>

            <div className="PagerInfo">
              {storesPage} de {storesTotalPages}
            </div>

            <button
              type="button"
              className="PagerButton"
              disabled={isLoading || storesPage >= storesTotalPages || saving}
              onClick={() => setStoresPage((p) => Math.min(storesTotalPages, p + 1))}
            >
              Próx.
            </button>
          </div>

          <div className="ModalActions">
            <button
              type="button"
              className="ModalButton ModalButtonCancel"
              onClick={onClose}
              disabled={saving}
            >
              Cancelar
            </button>

            <button
              type="submit"
              className="ModalButton ModalButtonConfirm"
              disabled={!canSubmit}
            >
              {saving ? "Salvando..." : "Confirmar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssignFreelancersToStoresModal;
